import { getLocalizedValue } from "@repo/shared";
import type { RiskLevel, RiskTimeHorizon } from "@repo/shared/types";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useUserLanguage } from "@/lib/i18n/context";
import { OptionCard } from "./option-card";

/**
 * One labelled selection section of the risk assessment — the time horizon or
 * the risk tolerance.
 *
 * The two are the same widget over different option lists, so they share one
 * component rather than two byte-identical files: what differs between them is
 * the heading and which list of the model's options they render. QL-212 lets
 * them appear on different pages (one per goal, one per client), which is
 * exactly why neither may assume the other is on screen.
 */
export function RiskOptionSection({
  heading,
  options,
  selected,
  onSelect,
  idPrefix,
  isCardsMode,
  disabled,
  language,
}: {
  heading: string;
  options: (RiskTimeHorizon | RiskLevel)[];
  selected: string;
  onSelect: (id: string) => void;
  idPrefix: string;
  /** QP-180 — "cards" renders rich selectable cards; otherwise a radio list. */
  isCardsMode: boolean;
  disabled: boolean;
  language: string;
}) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const sorted = [...options].sort((a, b) => a.order - b.order);

  return (
    <div className="flex flex-col gap-3">
      <Label className="text-base font-semibold">{heading}</Label>
      {isCardsMode ? (
        <fieldset
          aria-label={heading}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-0 p-0 m-0"
        >
          {sorted.map((option) => (
            <OptionCard
              key={option.id}
              selected={selected === option.id}
              disabled={disabled}
              label={getLocalizedValue(option.labelI18n, language, tenantDefaultLanguage)}
              description={
                option.descriptionI18n
                  ? getLocalizedValue(option.descriptionI18n, language, tenantDefaultLanguage)
                  : null
              }
              iconSvg={option.iconSvg}
              onSelect={() => onSelect(option.id)}
            />
          ))}
        </fieldset>
      ) : (
        <RadioGroup value={selected} onValueChange={onSelect} disabled={disabled}>
          {sorted.map((option) => (
            <OptionRow key={option.id}>
              <RadioGroupItem value={option.id} id={`${idPrefix}-${option.id}`} />
              <Label htmlFor={`${idPrefix}-${option.id}`} className="font-normal cursor-pointer">
                {getLocalizedValue(option.labelI18n, language, tenantDefaultLanguage)}
              </Label>
            </OptionRow>
          ))}
        </RadioGroup>
      )}
    </div>
  );
}
